import { AdminHome } from "@admin/adminHome";
import { AdminInventory } from "@admin/Inventory/admin-inventory";
import { CustomerContainer } from "@customer/customerContainer";
import { HomePage } from "@customer/HomePage/homepage";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { ROUTES } from "./routes";
import { ProtectedRoute } from "@components/Auth/ProtectedRoute";
import { AdminRoute } from "@components/Auth/AdminRoute";
import { ApprovalGate } from "@components/Auth/ApprovalGate";
import { AdminInventoryProducts } from "@admin/Inventory/products";
import { NewProductPage } from "@admin/Inventory/NewProduct/NewProductPage";
import { EditProductPage } from "@admin/Inventory/EditProduct/EditProductPage";
import { AdminCategories } from "@admin/Inventory/admin-categories";
import { Drydock } from "@/Features/Drydock/drydock";
import { Title } from "@common/index";
import { ProductDetail } from "@customer/Shopping/ProductDetail/ProductDetail";
import { Cart } from "@customer/Shopping/Cart/Cart";
import { Checkout } from "@customer/Shopping/Checkout/Checkout";
import { OrderConfirmation } from "@customer/Shopping/Confirmation/OrderConfirmation";
import { BrowseHome } from "@customer/Browse/BrowseHome";
import { BrowseAllCategories } from "@customer/Browse/BrowseAllCategories";
import { BrowsePopular } from "@customer/Browse/BrowsePopular";
import { BrowseFeatured } from "@customer/Browse/BrowseFeatured";
import { BrowseSubcategory } from "@customer/Browse/BrowseSubcategory";
import { SearchResults } from "@customer/Search/SearchResults";
import { VendorPage } from "@customer/Vendor/VendorPage";
import { AboutPage } from "@customer/Marketing/AboutPage";
import { ApplyPage } from "@customer/Marketing/ApplyPage";
import { ApplySuccessPage } from "@customer/Marketing/ApplySuccessPage";
import { InviteLandingPage } from "@customer/Marketing/InviteLandingPage";
import { SupportPage } from "@customer/Marketing/SupportPage";
import { TermsPage } from "@customer/Marketing/TermsPage";
import { PrivacyPage } from "@customer/Marketing/PrivacyPage";
import { NotFoundPage } from "@customer/ErrorPages/NotFoundPage";
import { AccountLayout } from "@customer/Account/AccountLayout";
import { AccountDashboard } from "@customer/Account/AccountDashboard";
import { AccountLocations } from "@customer/Account/AccountLocations";
import { AccountLocationDetail } from "@customer/Account/AccountLocationDetail";
import { OrderHistory } from "@customer/Account/OrderHistory";
import { MembershipPage } from "@customer/Account/MembershipPage";
import { BillingHistory } from "@customer/Account/BillingHistory";
import { AccountProfile } from "@customer/Account/AccountProfile";
import { PendingPage } from "@customer/Pending/PendingPage";
import { PaymentRequiredPage } from "@customer/PaymentRequired/PaymentRequiredPage";
import { AdminNotFoundPage } from "@admin/ErrorPages/AdminNotFoundPage";
import { Clients } from "@admin/Clients/Clients";
import { ClientDetail } from "@admin/Clients/ClientDetail";
import { Orders } from "@admin/Orders/Orders";
import { OrderDetail } from "@admin/Orders/OrderDetail";
import { Dashboard } from "@admin/Dashboard/Dashboard";
import { UnconfirmedCharges } from "@admin/Billing/UnconfirmedCharges";
import { LegalClasses } from "@admin/LegalClasses/LegalClasses";
import { Promotions } from "@admin/Promotions/Promotions";

export const router = createBrowserRouter([
    {
        // Default path, NO AUTHENTICATION REQUIRED
        path: ROUTES.HOME,
        element: <CustomerContainer />,
        children: [{ element: <HomePage />, index: true }],
    },
    {
        // Public marketing pages, NO AUTHENTICATION REQUIRED.
        // Reuse the customer chrome (CustomerContainer = nav + footer) per DecisionLog §4.10 (E10).
        path: ROUTES.ABOUT,
        element: <CustomerContainer />,
        children: [{ element: <AboutPage />, index: true }],
    },
    {
        path: ROUTES.SUPPORT,
        element: <CustomerContainer />,
        children: [{ element: <SupportPage />, index: true }],
    },
    {
        // Public customer application form (E5-S4 #191) — NOT under ProtectedRoute.
        path: ROUTES.APPLY,
        element: <CustomerContainer />,
        children: [{ element: <ApplyPage />, index: true }],
    },
    {
        path: ROUTES.APPLY_SUCCESS,
        element: <CustomerContainer />,
        children: [{ element: <ApplySuccessPage />, index: true }],
    },
    {
        // Public signup-link landing (E5-S5 #192) — anonymous, NOT under
        // ProtectedRoute. Validates `?token=` then shows a set-password form.
        path: ROUTES.ONBOARD_INVITE,
        element: <CustomerContainer />,
        children: [{ element: <InviteLandingPage />, index: true }],
    },
    {
        path: ROUTES.TERMS,
        element: <CustomerContainer />,
        children: [{ element: <TermsPage />, index: true }],
    },
    {
        path: ROUTES.PRIVACY,
        element: <CustomerContainer />,
        children: [{ element: <PrivacyPage />, index: true }],
    },
    {
        // AUTHENTICATION REQUIRED
        // Customer routes
        path: ROUTES.CUSTOMER,
        element: (
            <ProtectedRoute
                redirectUri={`${window.location.origin}${ROUTES.CUSTOMER}`}
            >
                <CustomerContainer />
            </ProtectedRoute>
        ),
        children: [
            // Reachable regardless of approval status (E5-S6): the account
            // subtree (so users can view/fix their profile) and the pending
            // wall itself. These sit OUTSIDE the ApprovalGate.
            // AccountLayout (#162) renders the persistent account tab strip
            // above each of these pages.
            {
                path: "account",
                element: <AccountLayout />,
                children: [
                    { index: true, element: <AccountDashboard /> },
                    { path: "orders", element: <OrderHistory /> },
                    { path: "locations", element: <AccountLocations /> },
                    {
                        path: "locations/:id",
                        element: <AccountLocationDetail />,
                    },
                    { path: "membership", element: <MembershipPage /> },
                    { path: "billing", element: <BillingHistory /> },
                    { path: "profile", element: <AccountProfile /> },
                ],
            },
            // `/c/settings` is retired (E12-S4 #239) — the /c/account hub
            // supersedes it. These redirects sit OUTSIDE the ApprovalGate, next
            // to `account`, deliberately: inside the gate a not-yet-approved
            // user would hit the approval wall instead of being forwarded to
            // the new URL. `replace` keeps the dead URL out of history.
            {
                path: "settings",
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate to={ROUTES.ACCOUNT_PROFILE} replace />
                        ),
                    },
                    {
                        path: "organizations",
                        element: (
                            <Navigate to={ROUTES.ACCOUNT_PROFILE} replace />
                        ),
                    },
                    {
                        path: "locations",
                        element: (
                            <Navigate to={ROUTES.ACCOUNT_LOCATIONS} replace />
                        ),
                    },
                ],
            },
            {
                path: "pending",
                element: <PendingPage />,
            },
            // Payment-required wall (#321) — like `pending`, it sits OUTSIDE
            // the ApprovalGate so a walled user can still reach it.
            {
                path: "payment-required",
                element: <PaymentRequiredPage />,
            },
            // Everything else is gated: not-yet-approved users are redirected
            // to /c/pending by the ApprovalGate (pathless layout route).
            {
                element: <ApprovalGate />,
                children: [
                    // Bare `/c` lands on the browse home. Inside the gate, so
                    // the gate decides before any redirect.
                    {
                        index: true,
                        element: <Navigate to={ROUTES.BROWSE} replace />,
                    },
                    {
                        path: "shopping",
                        children: [
                            // The legacy product table is retired: browse is
                            // the catalogue. `replace` keeps the dead URL out
                            // of history.
                            {
                                index: true,
                                element: (
                                    <Navigate to={ROUTES.BROWSE} replace />
                                ),
                            },
                            {
                                path: ":productId",
                                element: <ProductDetail />,
                            },
                            {
                                path: "cart",
                                element: <Cart />,
                            },
                            {
                                path: "checkout",
                                element: <Checkout />,
                            },
                            {
                                path: "order/:orderId",
                                element: <OrderConfirmation />,
                            },
                        ],
                    },
                    {
                        path: "browse",
                        children: [
                            { index: true, element: <BrowseHome /> },
                            // The home page's "View all" pages (#401),
                            // declared before the `:categoryId` param route.
                            {
                                path: "categories",
                                element: <BrowseAllCategories />,
                            },
                            { path: "popular", element: <BrowsePopular /> },
                            { path: "featured", element: <BrowseFeatured /> },
                            {
                                path: ":categoryId",
                                element: <BrowseSubcategory />,
                            },
                        ],
                    },
                    {
                        path: "search",
                        element: <SearchResults />,
                    },
                    // A vendor's page (#425): its name and the search grid
                    // with the vendor fixed.
                    {
                        path: "v/:vendorSlug",
                        element: <VendorPage />,
                    },
                ],
            },
        ],
    },
    {
        // AUTHENTICATION REQUIRED + Admin role (#401). AdminRoute renders the
        // customer 404 for a signed-in non-admin.
        path: ROUTES.ADMIN,
        element: (
            <ProtectedRoute
                redirectUri={`${window.location.origin}${ROUTES.ADMIN}`}
            >
                <AdminRoute>
                    <AdminHome />
                </AdminRoute>
            </ProtectedRoute>
        ),
        children: [
            { index: true, element: <Navigate to="dashboard" replace /> },
            {
                path: "dashboard",
                element: <Dashboard />,
            },
            {
                path: "inventory",
                element: <AdminInventory />,
                children: [
                    {
                        path: "products",
                        element: <AdminInventoryProducts />,
                    },
                    {
                        path: "products/new",
                        element: <NewProductPage />,
                    },
                    {
                        // Static `products/new` is declared first so it wins
                        // over this `:id` param route.
                        path: "products/:id",
                        element: <EditProductPage />,
                    },
                    {
                        path: "categories",
                        element: <AdminCategories />,
                    },
                    {
                        path: "import",
                        element: (
                            <div className="text-primary">
                                <Title>Import</Title>
                                <p>
                                    TBD: The import feature allows you to bulk
                                    upload products, categories, or modifiers
                                    from a CSV file. This can save time when
                                    adding multiple items to the inventory.
                                </p>
                            </div>
                        ),
                    },
                ],
            },
            { path: "orders", element: <Orders /> },
            { path: "orders/:id", element: <OrderDetail /> },
            { path: "legal-classes", element: <LegalClasses /> },
            { path: "promotions", element: <Promotions /> },
            { path: "clients", element: <Clients /> },
            { path: "clients/:id", element: <ClientDetail /> },
            // Admin-only server-side; the worklist for charge-outcome blocks
            // (#377) with the void-vs-release resolution flow (#382).
            {
                path: "billing/unconfirmed-charges",
                element: <UnconfirmedCharges />,
            },
            // Nested admin catch-all: renders the admin-branded 404 inside
            // AdminHome's chrome for any unmatched path under `/a/*`. Must stay
            // last so concrete admin children are matched first. The root
            // `{ path: "*" }` below continues to handle everything else.
            { path: "*", element: <AdminNotFoundPage /> },
        ],
    },
    {
        // Catch-all 404 route. Reuse the customer chrome (CustomerContainer =
        // nav + footer) per the marketing-page pattern so the user can still
        // navigate. The 404 renders inside RouterProvider, so it uses the
        // polymorphic Button/Link normally. The child is a splat too, not an
        // index route: a `*` parent consumes none of the URL, so an index child
        // matches only `/` (which the home route owns) and every unknown URL
        // rendered the chrome around an empty <main> (#472).
        path: "*",
        element: <CustomerContainer />,
        children: [{ path: "*", element: <NotFoundPage /> }],
    },
    {
        // Dev workbench routes
        path: "drydock",
        element: <Drydock />,
    },
]);
